// Sponsor package catalog. Prices, percentages and point values are examples
// from the LoyaltyBridge Sponsor Plan; the manager sets the real ones (decision D3).

export type TierId = 'starter' | 'growth' | 'premium';
export type Segment = 'biz' | 'ngo';
export type PayMethod = 'khqr' | 'bank' | 'card';

export interface Benefit {
  included: boolean;
  label: string;
}

export interface Tier {
  id: TierId;
  name: string;
  isNew?: boolean;
  featured?: boolean;
  desc: string;
  /** Monthly price (USD) at each views stop. */
  prices: number[];
  /** Views per month, in thousands, at each stop. */
  stops: number[];
  apps: string[];
  surveyAnswers: number;
  includedPoints: number;
  sponsorPrice: boolean;
  groups: { title: string; items: Benefit[] }[];
}

const b = (included: boolean, label: string): Benefit => ({ included, label });

export const TIERS: Tier[] = [
  {
    id: 'starter',
    name: 'Starter',
    desc: 'Try one app with banner ads. For a shop or small brand.',
    prices: [300, 480],
    stops: [50, 100],
    apps: ['Survey app'],
    surveyAnswers: 0,
    includedPoints: 0,
    sponsorPrice: false,
    groups: [
      { title: 'Where your ad shows', items: [b(true, '1 app of your choice'), b(true, 'Home screen banner'), b(false, 'Feed posts and sponsored surveys'), b(false, 'Choose provinces')] },
      { title: 'Points and rewards', items: [b(false, 'Logo on a reward'), b(false, 'Points budget for users'), b(true, 'Buy points packs at list price')] },
      { title: 'Reports', items: [b(true, 'Monthly report'), b(false, 'Weekly report')] },
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    featured: true,
    desc: 'Banner and native ads in both apps, plus your own survey. For banks, telecoms and drink brands.',
    prices: [800, 1280, 1800],
    stops: [150, 300, 500],
    apps: ['IDI app', 'Survey app'],
    surveyAnswers: 500,
    includedPoints: 20000,
    sponsorPrice: true,
    groups: [
      { title: 'Where your ad shows', items: [b(true, 'IDI + Survey app'), b(true, 'Banner + feed posts (native ads)'), b(true, 'In-app push, max 1 a day'), b(true, 'Choose provinces'), b(false, 'Full-screen ad (interstitial)')] },
      { title: 'Points and rewards', items: [b(true, '1 sponsored survey, 500 answers'), b(true, 'Logo on 1 reward at a partner shop'), b(true, '20,000 pt for users included'), b(true, 'Points packs at sponsor price, save 10%')] },
      { title: 'Reports', items: [b(true, 'Live dashboard + monthly report'), b(false, 'Weekly report')] },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    isNew: true,
    desc: 'Every connected app, full-screen ads and push messages. For big brands and ministry campaigns.',
    prices: [2000, 3200, 5400],
    stops: [500, 1000, 2000],
    apps: ['IDI app', 'Survey app', 'Next app'],
    surveyAnswers: 2000,
    includedPoints: 500000,
    sponsorPrice: true,
    groups: [
      { title: 'Where your ad shows', items: [b(true, 'All connected apps'), b(true, 'Everything in Growth'), b(true, 'Full-screen ad, 7 days a month'), b(true, '2 push messages a month')] },
      { title: 'Points and rewards', items: [b(true, 'Survey with 2,000 answers'), b(true, '500,000 pt for users included'), b(true, 'Logo on 3 rewards'), b(true, 'Points packs at sponsor price, save 10%')] },
      { title: 'Reports', items: [b(true, 'Weekly report'), b(true, 'Own OPS contact person')] },
    ],
  },
];

export interface Period {
  months: number;
  label: string;
  tag?: string;
  discount?: number;
  bonusMonths?: number;
}

export const PERIODS: Period[] = [
  { months: 1, label: '1 month' },
  { months: 3, label: '3 months', tag: 'save 10%', discount: 0.1 },
  { months: 6, label: '6 months', tag: '+1 month free', bonusMonths: 1 },
  { months: 12, label: '12 months', tag: 'save 30%', discount: 0.3 },
];

export const NGO_DISCOUNT = 0.15;
export const VAT_RATE = 0.1;
export const SPONSOR_PACK_DISCOUNT = 0.1;
/** Share of ad package money paid to app owners, split by views (decision D2). */
export const APP_OWNER_SHARE = 0.3;

export interface PointsPack {
  name: string;
  points: number;
  bonusPct: number;
  price: number;
}

export const POINTS_PACKS: PointsPack[] = [
  { name: 'Try it', points: 20000, bonusPct: 0, price: 60 },
  { name: 'Steady', points: 50000, bonusPct: 5, price: 150 },
  { name: 'Popular', points: 100000, bonusPct: 10, price: 300 },
  { name: 'Big campaign', points: 250000, bonusPct: 16, price: 750 },
  { name: 'Nationwide', points: 500000, bonusPct: 20, price: 1500 },
];

export interface BoostPack {
  name: string;
  points: number;
  price: number;
  limit: string;
}

export const BOOST_PACKS: BoostPack[] = [
  { name: 'Weekend push', points: 15000, price: 45, limit: 'Max 1 a week' },
  { name: 'Event boost', points: 30000, price: 85, limit: 'Max 1 a week' },
  { name: 'Campaign burst', points: 60000, price: 160, limit: 'Max 1 a week' },
  { name: 'Launch week', points: 120000, price: 300, limit: 'Max 1 a month' },
];

export const PROVINCES = ['Phnom Penh', 'Siem Reap', 'Battambang', 'Kampong Cham', 'Sihanoukville', 'All provinces'];

export const BUSINESS_TYPES = [
  'Bank or finance',
  'Telecom',
  'Food and drink brand',
  'Shop or service',
  'NGO',
  'Ministry or government',
];

/** Ad content OPS refuses (decision D4). */
export const BANNED_CATEGORIES = ['alcohol', 'tobacco', 'gambling', 'politics'];

export const tierById = (id: TierId): Tier => {
  const tier = TIERS.find((t) => t.id === id);
  if (!tier) throw new Error(`Unknown tier ${id}`);
  return tier;
};
